// Articulated boss/epic rigs: per-boss silhouettes with real limbs (hip/knee/shoulder/elbow
// joints), a shared giant gait (foot-plant events drive dust + camera rumble), melee
// windup->slam attacks, dual-blade slashes for raider bosses, hover machines for the Astral
// bosses, and a pick-up-and-crush kill flourish for giant-tier melee bosses.
// Fully procedural (no asset files, plan §29.4).

import * as THREE from 'three';
import type { PlayerId } from '../content/types';
import { OWNER_COLORS } from './unitStyles';

type Mat = THREE.MeshStandardMaterial;

export interface BossRig {
  group: THREE.Group;
  kind: 'biped' | 'hover';
  /** base group scale (uiScale / 1.7 — same convention as the old shared boss mesh) */
  scale: number;
  /** giant-tier presence: footfall rumble + crush flourish eligibility */
  big: boolean;
  isMelee: boolean;
  canCrush: boolean;
  dualWield: boolean;
  dustColors: number[];
  /** gait phase advance per world meter walked */
  stridePerMeter: number;
  strideAmp: number;
  hipW: number;
  legLen: number;
  /** resting torso pitch (hunched beasts) */
  hunch: number;
  rootY: number;
  root: THREE.Group;
  torso: THREE.Group;
  head: THREE.Group;
  hipL?: THREE.Group;
  hipR?: THREE.Group;
  kneeL?: THREE.Group;
  kneeR?: THREE.Group;
  shL?: THREE.Group;
  shR?: THREE.Group;
  elL?: THREE.Group;
  elR?: THREE.Group;
  fistL?: THREE.Group;
  fistR?: THREE.Group;
  /** hidden "grabbed soldier" prop inside the left fist (crush flourish) */
  victim?: THREE.Mesh;
  /** continuously spinning parts (halo rings, gyros, pylon carousels) */
  spinners: THREE.Object3D[];
  /** war-engine turret gimbal + recoiling barrel + muzzle anchor */
  cannon?: THREE.Group;
  cannonInner?: THREE.Group;
  cannonRestZ: number;
  muzzleTip?: THREE.Object3D;
  /** arbiter staff arm (raises while channeling) */
  staffArm?: THREE.Group;
  /** emissive materials that pulse with windup and flash on fire */
  auraMats: Mat[];
  /** boss-specific secondary life that does not change the shared combat pose */
  idleMotion?: (time: number, moveAmt: number) => void;
  /** richer shared idle weight shift; disabled for owner-protected silhouettes */
  idleAccent: boolean;
}

export interface BossAnimState {
  walkPhase: number;
  prevCooldown: number;
  attackT: number;
  impactDone: boolean;
  yaw: number;
  crushT: number;
  crushBurstDone: boolean;
  /** dual-wield: which arm slashes next */
  parity: number;
  /** fire flash 1 -> 0 */
  flash: number;
  spawnT: number;
  seed: number;
}

export interface BossFrameInput {
  t: number;
  dt: number;
  x: number;
  z: number;
  speed: number;
  moveAmt: number;
  /** 0..1 attack windup progress (cooldown approaching zero with a live target) */
  winding: number;
  fired: boolean;
  /** smoothed world facing */
  facing: number;
  /** world angle from boss to its current target, if any */
  targetYaw: number | null;
  /** world-units strike distance for the slam impact fx */
  reach: number;
  startCrush: boolean;
}

export interface BossFrameEvents {
  footfall?: { x: number; z: number };
  impact?: { x: number; z: number };
  crush?: { x: number; y: number; z: number };
  muzzle?: { x: number; y: number; z: number };
}

const SLAM_T = 0.16;
const RECOVER_T = 0.9;
const CRUSH_DUR = 1.5;
// shoulder rotation.x convention: 0 = hanging, positive = backward, negative = forward.
// The chop raises the arm up over the back (+2.6) and swings it over the top of the arc,
// ending forward-down (+5.6 ≡ -0.68) — recovery then lowers from the wrapped value.
const ARM_REST = 0.18;
const ARM_RAISED = 2.6;
const ARM_SLAM_END = 5.6;
const ARM_SLAM_REC = ARM_SLAM_END - Math.PI * 2;

export function newBossAnim(id: number, facing: number): BossAnimState {
  return {
    walkPhase: (id * 0.618) % 6.28,
    prevCooldown: 0,
    attackT: 9,
    impactDone: true,
    yaw: facing,
    crushT: CRUSH_DUR,
    crushBurstDone: true,
    parity: 0,
    flash: 0,
    spawnT: 0,
    seed: (id * 1.37) % 6.28,
  };
}

function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * Math.min(1, t);
}

function wrapPi(a: number): number {
  let d = a % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return d;
}

function M(color: number, rough = 0.6, metal = 0.2): Mat {
  return new THREE.MeshStandardMaterial({ color, roughness: rough, metalness: metal });
}

function glowM(color: number, intensity: number): Mat {
  const m = new THREE.MeshStandardMaterial({
    color,
    emissive: color,
    emissiveIntensity: intensity,
    roughness: 0.3,
    metalness: 0.1,
  });
  m.userData.baseEI = intensity;
  return m;
}

function mix(a: number, b: number, t: number): number {
  return new THREE.Color(a).lerp(new THREE.Color(b), t).getHex();
}

// ---------------------------------------------------------------- skeleton builders

interface BipedOpts {
  thigh: number;
  shin: number;
  legR: number;
  hipW: number;
  upper: number;
  fore: number;
  armR: number;
  fistR: number;
  shoulderW: number;
  shoulderY: number;
  headY: number;
  headZ?: number;
  legMat: Mat;
  armMat: Mat;
  fistMat: Mat;
}

interface Skeleton {
  group: THREE.Group;
  root: THREE.Group;
  torso: THREE.Group;
  head: THREE.Group;
  hipL: THREE.Group;
  hipR: THREE.Group;
  kneeL: THREE.Group;
  kneeR: THREE.Group;
  shL: THREE.Group;
  shR: THREE.Group;
  elL: THREE.Group;
  elR: THREE.Group;
  fistL: THREE.Group;
  fistR: THREE.Group;
  legLen: number;
}

function taperCyl(rTop: number, rBot: number, h: number): THREE.CylinderGeometry {
  return new THREE.CylinderGeometry(rTop, rBot, h, 7);
}

function buildBiped(o: BipedOpts): Skeleton {
  const group = new THREE.Group();
  const root = new THREE.Group();
  const legLen = o.thigh + o.shin - 0.06;
  root.position.y = legLen;
  group.add(root);

  const mkLeg = (side: number): { hip: THREE.Group; knee: THREE.Group } => {
    const hip = new THREE.Group();
    hip.position.set(side * o.hipW, 0, 0);
    const thigh = new THREE.Mesh(taperCyl(o.legR * 1.1, o.legR * 0.9, o.thigh), o.legMat);
    thigh.position.y = -o.thigh / 2;
    hip.add(thigh);
    const knee = new THREE.Group();
    knee.position.y = -o.thigh;
    const shin = new THREE.Mesh(taperCyl(o.legR * 0.85, o.legR * 1.05, o.shin), o.legMat);
    shin.position.y = -o.shin / 2;
    knee.add(shin);
    const foot = new THREE.Mesh(new THREE.BoxGeometry(o.legR * 2.6, o.legR * 1.1, o.legR * 3.6), o.fistMat);
    foot.position.set(0, -o.shin + o.legR * 0.4, o.legR * 0.9);
    knee.add(foot);
    hip.add(knee);
    root.add(hip);
    return { hip, knee };
  };
  const legL = mkLeg(-1);
  const legR = mkLeg(1);

  const torso = new THREE.Group();
  torso.position.y = 0.02;
  root.add(torso);
  const head = new THREE.Group();
  head.position.set(0, o.headY, o.headZ ?? 0);
  torso.add(head);

  const mkArm = (side: number): { sh: THREE.Group; el: THREE.Group; fist: THREE.Group } => {
    const sh = new THREE.Group();
    sh.position.set(side * o.shoulderW, o.shoulderY, 0);
    const upper = new THREE.Mesh(taperCyl(o.armR * 1.1, o.armR * 0.9, o.upper), o.armMat);
    upper.position.y = -o.upper / 2;
    sh.add(upper);
    const el = new THREE.Group();
    el.position.y = -o.upper;
    const fore = new THREE.Mesh(taperCyl(o.armR * 0.85, o.armR, o.fore), o.armMat);
    fore.position.y = -o.fore / 2;
    el.add(fore);
    const fist = new THREE.Group();
    fist.position.y = -o.fore;
    const knuckle = new THREE.Mesh(new THREE.SphereGeometry(o.fistR, 8, 6), o.fistMat);
    fist.add(knuckle);
    el.add(fist);
    sh.add(el);
    torso.add(sh);
    return { sh, el, fist };
  };
  const armL = mkArm(-1);
  const armR = mkArm(1);

  return {
    group,
    root,
    torso,
    head,
    hipL: legL.hip,
    hipR: legR.hip,
    kneeL: legL.knee,
    kneeR: legR.knee,
    shL: armL.sh,
    shR: armR.sh,
    elL: armL.el,
    elR: armR.el,
    fistL: armL.fist,
    fistR: armR.fist,
    legLen,
  };
}

// ---------------------------------------------------------------- shared props

function addBlobShadow(group: THREE.Group, radius: number): void {
  const blob = new THREE.Mesh(
    new THREE.CircleGeometry(radius, 18),
    new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.22, depthWrite: false }),
  );
  blob.rotation.x = -Math.PI / 2;
  blob.position.y = 0.02;
  blob.renderOrder = 2;
  group.add(blob);
}

function addBackBanner(torso: THREE.Group, owner: 0 | 1, y: number, size: number): void {
  const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 1.6 * size, 5), M(0x3a3d44, 0.6, 0.5));
  pole.position.set(0, y, -0.42);
  pole.rotation.x = -0.12;
  torso.add(pole);
  const flag = new THREE.Mesh(
    new THREE.PlaneGeometry(0.6 * size, 0.85 * size),
    new THREE.MeshStandardMaterial({ color: OWNER_COLORS[owner], roughness: 0.85, side: THREE.DoubleSide }),
  );
  flag.position.set(0.32 * size, y + 0.55 * size, -0.52);
  torso.add(flag);
}

function addCape(torso: THREE.Group, mat: Mat, w: number, h: number, y: number): void {
  const cape = new THREE.Mesh(new THREE.PlaneGeometry(w, h), mat);
  cape.position.set(0, y - h / 2, -0.38);
  cape.rotation.x = 0.16;
  torso.add(cape);
}

/** Hammer gripped in a fist: shaft continues down the forearm axis, head at the end. */
function addHammer(fist: THREE.Group, shaftLen: number, headW: number, shaftMat: Mat, headMat: Mat): void {
  const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, shaftLen, 6), shaftMat);
  shaft.position.y = -shaftLen / 2 + 0.25;
  fist.add(shaft);
  const head = new THREE.Mesh(new THREE.BoxGeometry(headW, headW * 0.62, headW * 0.62), headMat);
  head.position.y = -shaftLen + 0.32;
  fist.add(head);
  const spike = new THREE.Mesh(new THREE.ConeGeometry(headW * 0.2, headW * 0.5, 5), headMat);
  spike.position.y = -shaftLen + 0.32 - headW * 0.55;
  spike.rotation.x = Math.PI;
  fist.add(spike);
}

function addSword(fist: THREE.Group, bladeLen: number, bladeMat: Mat, guardMat: Mat): void {
  const guard = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.08, 0.12), guardMat);
  guard.position.y = -0.18;
  fist.add(guard);
  const blade = new THREE.Mesh(new THREE.BoxGeometry(0.16, bladeLen, 0.045), bladeMat);
  blade.position.y = -0.2 - bladeLen / 2;
  fist.add(blade);
  const tip = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.28, 4), bladeMat);
  tip.rotation.x = Math.PI;
  tip.rotation.y = Math.PI / 4;
  tip.position.y = -0.2 - bladeLen - 0.13;
  fist.add(tip);
}

function addClaws(fist: THREE.Group, clawMat: Mat, len: number): void {
  for (const cx of [-0.09, 0, 0.09]) {
    const claw = new THREE.Mesh(new THREE.ConeGeometry(0.035, len, 5), clawMat);
    claw.position.set(cx, -len / 2 - 0.08, 0.05);
    claw.rotation.x = Math.PI;
    fist.add(claw);
  }
}

function addFoliage(parent: THREE.Object3D, mat: Mat, x: number, y: number, z: number, r: number): void {
  const clump = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 0), mat);
  clump.position.set(x, y, z);
  clump.rotation.set(x * 3.1, y * 2.3, z * 1.7);
  parent.add(clump);
}

function addVictimProp(fist: THREE.Group, fistR: number): THREE.Mesh {
  const victim = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.14, 0.34, 3, 6),
    new THREE.MeshStandardMaterial({ color: 0x8f96a3, roughness: 0.8 }),
  );
  victim.rotation.z = Math.PI / 2;
  victim.position.y = -fistR - 0.16;
  victim.visible = false;
  fist.add(victim);
  return victim;
}

function enableShadows(group: THREE.Group): void {
  group.traverse((o) => {
    if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
}

const EARTH_DUST = [0x8a7f6a, 0x6e6454, 0x9a8d76];
const LEAF_DUST = [0x5f7a44, 0x6e6454, 0x7a9a52];
const BONE_DUST = [0x8a8578, 0x6e6454, 0xa8a094];
const LIGHT_DUST = [0x9aa4b8, 0x8a94a8, 0xb8c2d4];

function baseRig(skel: Skeleton, uiScale: number, over: Partial<BossRig>): BossRig {
  const scale = uiScale / 1.7;
  skel.group.scale.setScalar(scale);
  const rig: BossRig = {
    group: skel.group,
    kind: 'biped',
    scale,
    big: uiScale >= 1.9,
    isMelee: true,
    canCrush: false,
    dualWield: false,
    dustColors: EARTH_DUST,
    stridePerMeter: 2.6 / (skel.legLen * scale),
    strideAmp: 0.52,
    hipW: 0.45,
    legLen: skel.legLen,
    hunch: 0,
    rootY: skel.legLen,
    root: skel.root,
    torso: skel.torso,
    head: skel.head,
    hipL: skel.hipL,
    hipR: skel.hipR,
    kneeL: skel.kneeL,
    kneeR: skel.kneeR,
    shL: skel.shL,
    shR: skel.shR,
    elL: skel.elL,
    elR: skel.elR,
    fistL: skel.fistL,
    fistR: skel.fistR,
    spinners: [],
    cannonRestZ: 0,
    auraMats: [],
    idleAccent: true,
    ...over,
  };
  return rig;
}

// ---------------------------------------------------------------- Iron Covenant

function icArcturus(owner: 0 | 1, uiScale: number): BossRig {
  const steel = M(mix(0x9aa2b0, OWNER_COLORS[owner], 0.18), 0.38, 0.7);
  const dark = M(0x3f434c, 0.5, 0.55);
  const gold = M(0xd8b04a, 0.3, 0.8);
  const skel = buildBiped({
    thigh: 0.85, shin: 0.75, legR: 0.17, hipW: 0.42,
    upper: 0.7, fore: 0.62, armR: 0.14, fistR: 0.19,
    shoulderW: 0.72, shoulderY: 1.62, headY: 1.95,
    legMat: dark, armMat: steel, fistMat: dark,
  });
  const chest = new THREE.Mesh(new THREE.BoxGeometry(1.15, 1.05, 0.68), steel);
  chest.position.y = 1.25;
  skel.torso.add(chest);
  const plate = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.55, 0.1), gold);
  plate.position.set(0, 1.35, 0.36);
  skel.torso.add(plate);
  const waist = new THREE.Mesh(taperCyl(0.4, 0.52, 0.6), dark);
  waist.position.y = 0.45;
  skel.torso.add(waist);
  for (const sx of [-1, 1]) {
    const pauldron = new THREE.Mesh(new THREE.SphereGeometry(0.32, 8, 6), gold);
    pauldron.position.set(sx * 0.74, 1.72, 0);
    skel.torso.add(pauldron);
  }
  // commander helm + plume
  const helm = new THREE.Mesh(new THREE.SphereGeometry(0.32, 10, 8), steel);
  skel.head.add(helm);
  const visor = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.09, 0.12), dark);
  visor.position.set(0, 0.02, 0.28);
  skel.head.add(visor);
  const plume = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.65, 6), M(0xb03a2e, 0.9, 0));
  plume.position.set(0, 0.48, -0.08);
  plume.rotation.x = -0.35;
  skel.head.add(plume);
  addHammer(skel.fistR, 1.5, 0.52, M(0x6a5236, 0.8, 0.1), steel);
  addBackBanner(skel.torso, owner, 1.55, 1.0);
  addBlobShadow(skel.group, 1.15);
  enableShadows(skel.group);
  return baseRig(skel, uiScale, {});
}

function icIronSaint(owner: 0 | 1, uiScale: number): BossRig {
  const plate = M(mix(0xcfd6e4, OWNER_COLORS[owner], 0.12), 0.35, 0.75);
  const dark = M(0x4a505c, 0.5, 0.6);
  const gold = M(0xd8b04a, 0.3, 0.8);
  const halo = glowM(0xffe9a0, 1.4);
  const skel = buildBiped({
    thigh: 0.9, shin: 0.8, legR: 0.2, hipW: 0.46,
    upper: 0.78, fore: 0.68, armR: 0.17, fistR: 0.22,
    shoulderW: 0.85, shoulderY: 1.7, headY: 2.08,
    legMat: dark, armMat: plate, fistMat: dark,
  });
  const chest = new THREE.Mesh(new THREE.BoxGeometry(1.35, 1.15, 0.8), plate);
  chest.position.y = 1.3;
  skel.torso.add(chest);
  const sigil = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.06, 8), halo);
  sigil.rotation.x = Math.PI / 2;
  sigil.position.set(0, 1.42, 0.44);
  skel.torso.add(sigil);
  const skirt = new THREE.Mesh(taperCyl(0.45, 0.62, 0.7), dark);
  skirt.position.y = 0.42;
  skel.torso.add(skirt);
  for (const sx of [-1, 1]) {
    const pauldron = new THREE.Mesh(new THREE.SphereGeometry(0.4, 8, 6), gold);
    pauldron.position.set(sx * 0.88, 1.82, 0);
    skel.torso.add(pauldron);
    const rim = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.4, 5), gold);
    rim.position.set(sx * 1.02, 2.05, 0);
    rim.rotation.z = sx * -0.5;
    skel.torso.add(rim);
  }
  const helm = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), plate);
  skel.head.add(helm);
  const crest = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.34, 0.5), gold);
  crest.position.y = 0.28;
  skel.head.add(crest);
  // saint's halo behind the head
  const haloRing = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.045, 6, 24), halo);
  haloRing.position.set(0, 0.25, -0.3);
  skel.head.add(haloRing);
  addSword(skel.fistR, 1.7, plate, gold);
  // tower shield strapped to the left forearm
  const shield = new THREE.Mesh(new THREE.BoxGeometry(0.95, 1.5, 0.1), M(mix(0x4a566e, OWNER_COLORS[owner], 0.45), 0.5, 0.5));
  shield.position.set(-0.16, -0.35, 0.12);
  skel.elL.add(shield);
  addBlobShadow(skel.group, 1.3);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, {});
  rig.auraMats.push(halo);
  return rig;
}

function icCrownColossus(owner: 0 | 1, uiScale: number): BossRig {
  const bronze = M(mix(0xb08d4a, OWNER_COLORS[owner], 0.12), 0.4, 0.75);
  const iron = M(0x555b66, 0.45, 0.65);
  const dark = M(0x33373f, 0.55, 0.55);
  const furnace = glowM(0xff9c3f, 1.6);
  const skel = buildBiped({
    thigh: 1.0, shin: 0.9, legR: 0.3, hipW: 0.55,
    upper: 0.95, fore: 0.85, armR: 0.24, fistR: 0.3,
    shoulderW: 1.05, shoulderY: 1.85, headY: 2.3,
    legMat: iron, armMat: bronze, fistMat: dark,
  });
  const chest = new THREE.Mesh(new THREE.BoxGeometry(1.8, 1.35, 1.05), bronze);
  chest.position.y = 1.45;
  skel.torso.add(chest);
  // glowing furnace grate in the chest
  const grate = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.5, 0.08), furnace);
  grate.position.set(0, 1.5, 0.56);
  skel.torso.add(grate);
  for (const gy of [1.32, 1.5, 1.68]) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.05, 0.1), dark);
    bar.position.set(0, gy, 0.58);
    skel.torso.add(bar);
  }
  const waist = new THREE.Mesh(taperCyl(0.55, 0.75, 0.75), dark);
  waist.position.y = 0.45;
  skel.torso.add(waist);
  for (const sx of [-1, 1]) {
    const tower = new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.55, 0.7), iron);
    tower.position.set(sx * 1.15, 2.0, 0);
    skel.torso.add(tower);
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.5, 5), bronze);
    spike.position.set(sx * 1.15, 2.5, 0);
    skel.torso.add(spike);
  }
  // crowned helm
  const helm = new THREE.Mesh(taperCyl(0.3, 0.36, 0.5), iron);
  skel.head.add(helm);
  const crownBase = new THREE.Mesh(new THREE.CylinderGeometry(0.34, 0.3, 0.14, 8), bronze);
  crownBase.position.y = 0.3;
  skel.head.add(crownBase);
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2;
    const point = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.3, 4), bronze);
    point.position.set(Math.cos(a) * 0.28, 0.5, Math.sin(a) * 0.28);
    skel.head.add(point);
  }
  const eyes = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.07, 0.1), furnace);
  eyes.position.set(0, 0.05, 0.32);
  skel.head.add(eyes);
  // the Kingbreaker: a colossal two-handed hammer in the right fist
  addHammer(skel.fistR, 2.1, 0.85, dark, bronze);
  const victim = addVictimProp(skel.fistL, 0.3);
  addBlobShadow(skel.group, 1.6);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, { canCrush: true, victim, hipW: 0.55, strideAmp: 0.46 });
  rig.auraMats.push(furnace);
  return rig;
}

// ---------------------------------------------------------------- Ashen Host

function ahVeyr(owner: 0 | 1, uiScale: number): BossRig {
  const shroud = M(mix(0x4a4055, OWNER_COLORS[owner], 0.1), 0.85, 0.1);
  const pale = M(0x9a8fa5, 0.7, 0.15);
  const ember = glowM(0xff6a3f, 1.5);
  const skel = buildBiped({
    thigh: 0.8, shin: 0.75, legR: 0.12, hipW: 0.34,
    upper: 0.72, fore: 0.68, armR: 0.1, fistR: 0.14,
    shoulderW: 0.58, shoulderY: 1.55, headY: 1.82, headZ: 0.12,
    legMat: shroud, armMat: shroud, fistMat: pale,
  });
  const chest = new THREE.Mesh(new THREE.CapsuleGeometry(0.4, 0.7, 3, 8), shroud);
  chest.position.y = 1.15;
  skel.torso.add(chest);
  // spiked collar
  for (const sx of [-1, 1]) {
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.4, 5), pale);
    spike.position.set(sx * 0.5, 1.72, -0.05);
    spike.rotation.z = sx * -0.7;
    skel.torso.add(spike);
  }
  addCape(skel.torso, M(0x2e2836, 0.95, 0), 1.0, 1.7, 1.65);
  // hooded head with ember eyes
  const hood = new THREE.Mesh(new THREE.ConeGeometry(0.3, 0.62, 7), shroud);
  hood.position.y = 0.16;
  skel.head.add(hood);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.045, 6, 5), ember);
    eye.position.set(sx * 0.1, 0.02, 0.22);
    skel.head.add(eye);
  }
  addClaws(skel.fistL, pale, 0.45);
  addClaws(skel.fistR, pale, 0.45);
  addBlobShadow(skel.group, 1.0);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, { dualWield: true, hunch: 0.32, strideAmp: 0.6, dustColors: BONE_DUST });
  rig.stridePerMeter *= 1.25; // quick, prowling steps
  rig.auraMats.push(ember);
  return rig;
}

function ahGraveboundColossus(owner: 0 | 1, uiScale: number): BossRig {
  const bone = M(0xcfc6b4, 0.8, 0.05);
  const grave = M(mix(0x5a5d66, OWNER_COLORS[owner], 0.1), 0.9, 0.1);
  const soul = glowM(0x7de8a0, 1.6);
  const skel = buildBiped({
    thigh: 0.95, shin: 0.85, legR: 0.24, hipW: 0.52,
    upper: 0.92, fore: 0.85, armR: 0.19, fistR: 0.24,
    shoulderW: 0.98, shoulderY: 1.8, headY: 2.2,
    legMat: grave, armMat: bone, fistMat: grave,
  });
  // spine + soul core wrapped in rib arcs
  const spine = new THREE.Mesh(taperCyl(0.2, 0.3, 1.7), grave);
  spine.position.y = 1.1;
  skel.torso.add(spine);
  const core = new THREE.Mesh(new THREE.SphereGeometry(0.34, 10, 8), soul);
  core.position.y = 1.35;
  skel.torso.add(core);
  for (let i = 0; i < 3; i++) {
    const rib = new THREE.Mesh(new THREE.TorusGeometry(0.55 - i * 0.06, 0.07, 6, 12, Math.PI * 1.35), bone);
    rib.position.y = 1.7 - i * 0.35;
    rib.rotation.x = Math.PI / 2;
    rib.rotation.z = Math.PI * 0.82;
    skel.torso.add(rib);
  }
  const pelvis = new THREE.Mesh(new THREE.BoxGeometry(1.0, 0.5, 0.7), grave);
  pelvis.position.y = 0.3;
  skel.torso.add(pelvis);
  for (const sx of [-1, 1]) {
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.6, 5), bone);
    spike.position.set(sx * 1.0, 2.05, -0.1);
    spike.rotation.z = sx * -0.55;
    skel.torso.add(spike);
  }
  // skull
  const skull = new THREE.Mesh(new THREE.SphereGeometry(0.32, 9, 7), bone);
  skel.head.add(skull);
  const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.18, 0.3), bone);
  jaw.position.set(0, -0.24, 0.1);
  skel.head.add(jaw);
  for (const sx of [-1, 1]) {
    const socket = new THREE.Mesh(new THREE.SphereGeometry(0.07, 6, 5), soul);
    socket.position.set(sx * 0.12, 0.04, 0.26);
    skel.head.add(socket);
  }
  // tombstone-slab fists
  for (const fist of [skel.fistL, skel.fistR]) {
    const slab = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.72, 0.3), grave);
    slab.position.y = -0.28;
    fist.add(slab);
  }
  const victim = addVictimProp(skel.fistL, 0.4);
  addBlobShadow(skel.group, 1.45);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, { canCrush: true, victim, hipW: 0.52, strideAmp: 0.48, dustColors: BONE_DUST });
  rig.auraMats.push(soul);
  return rig;
}

function ahHollowKing(owner: 0 | 1, uiScale: number): BossRig {
  const regalia = M(mix(0x4a4d5a, OWNER_COLORS[owner], 0.1), 0.5, 0.55);
  const bone = M(0xcfc6b4, 0.8, 0.05);
  const tarnished = M(0xa8925a, 0.45, 0.7);
  const voidGlow = glowM(0x6de89a, 1.7);
  const skel = buildBiped({
    thigh: 1.0, shin: 0.92, legR: 0.22, hipW: 0.48,
    upper: 0.9, fore: 0.8, armR: 0.17, fistR: 0.22,
    shoulderW: 0.92, shoulderY: 1.85, headY: 2.28,
    legMat: regalia, armMat: regalia, fistMat: bone,
  });
  const chest = new THREE.Mesh(new THREE.BoxGeometry(1.4, 1.3, 0.85), regalia);
  chest.position.y = 1.4;
  skel.torso.add(chest);
  // the empty-throne void burning in his chest
  const voidHole = new THREE.Mesh(new THREE.SphereGeometry(0.26, 9, 7), voidGlow);
  voidHole.position.set(0, 1.48, 0.4);
  skel.torso.add(voidHole);
  const skirt = new THREE.Mesh(taperCyl(0.5, 0.72, 0.85), M(0x33313d, 0.9, 0.1));
  skirt.position.y = 0.4;
  skel.torso.add(skirt);
  for (const sx of [-1, 1]) {
    const pauldron = new THREE.Mesh(new THREE.BoxGeometry(0.55, 0.4, 0.7), tarnished);
    pauldron.position.set(sx * 0.98, 1.98, 0);
    pauldron.rotation.z = sx * 0.15;
    skel.torso.add(pauldron);
  }
  addCape(skel.torso, M(0x2c2a36, 0.95, 0), 1.5, 2.1, 1.95);
  // gaunt crowned skull
  const skull = new THREE.Mesh(new THREE.SphereGeometry(0.3, 9, 7), bone);
  skel.head.add(skull);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.06, 6, 5), voidGlow);
    eye.position.set(sx * 0.11, 0.05, 0.25);
    skel.head.add(eye);
  }
  const crownBand = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.32, 0.16, 8), tarnished);
  crownBand.position.y = 0.28;
  skel.head.add(crownBand);
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const point = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.34, 4), tarnished);
    point.position.set(Math.cos(a) * 0.27, 0.5, Math.sin(a) * 0.27);
    skel.head.add(point);
  }
  addSword(skel.fistR, 2.0, bone, tarnished);
  const victim = addVictimProp(skel.fistL, 0.3);
  addBlobShadow(skel.group, 1.5);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, { canCrush: true, victim, strideAmp: 0.5, dustColors: BONE_DUST });
  rig.auraMats.push(voidGlow);
  return rig;
}

/**
 * Crimson Sovereign — Ashen apotheosis form A. Slender vampire lord: dark armor with
 * crimson accents, high collar, flowing cape, tattered wings, clawed hands, luminous
 * red eyes, and a blood-orb hovering over the left palm.
 */
function ahCrimsonSovereign(owner: 0 | 1, uiScale: number): BossRig {
  const armor = M(mix(0x2b2130, OWNER_COLORS[owner], 0.1), 0.45, 0.55);
  const crimson = M(0x7a1626, 0.5, 0.35);
  const pale = M(0xded2c8, 0.7, 0.05);
  const blood = glowM(0xff2440, 1.7);
  const membrane = new THREE.MeshStandardMaterial({
    color: mix(0x40141c, OWNER_COLORS[owner], 0.08),
    roughness: 0.9,
    side: THREE.DoubleSide,
  });
  const skel = buildBiped({
    thigh: 0.85, shin: 0.78, legR: 0.12, hipW: 0.32,
    upper: 0.72, fore: 0.66, armR: 0.095, fistR: 0.13,
    shoulderW: 0.56, shoulderY: 1.62, headY: 1.94,
    legMat: armor, armMat: armor, fistMat: pale,
  });
  const chest = new THREE.Mesh(new THREE.CapsuleGeometry(0.36, 0.68, 3, 8), armor);
  chest.position.y = 1.2;
  skel.torso.add(chest);
  // crimson sash + waist fauld
  const sash = new THREE.Mesh(new THREE.BoxGeometry(0.13, 0.95, 0.46), crimson);
  sash.position.set(0, 1.18, 0.02);
  sash.rotation.z = 0.5;
  skel.torso.add(sash);
  const fauld = new THREE.Mesh(taperCyl(0.32, 0.42, 0.4), crimson);
  fauld.position.y = 0.62;
  skel.torso.add(fauld);
  // high collar framing the head
  for (const sx of [-1, 1]) {
    const collar = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.52, 0.07), crimson);
    collar.position.set(sx * 0.24, 1.86, -0.2);
    collar.rotation.z = sx * -0.32;
    collar.rotation.x = -0.16;
    skel.torso.add(collar);
    const pauldron = new THREE.Mesh(new THREE.ConeGeometry(0.17, 0.42, 6), armor);
    pauldron.position.set(sx * 0.6, 1.74, 0);
    pauldron.rotation.z = sx * 1.35;
    skel.torso.add(pauldron);
  }
  addCape(skel.torso, M(0x1a1016, 0.95, 0), 1.05, 1.85, 1.72);
  // tattered wing pair sprouting from the back
  const wings: THREE.Group[] = [];
  for (const sx of [-1, 1]) {
    const wing = new THREE.Group();
    wing.position.set(sx * 0.28, 1.6, -0.3);
    wing.rotation.y = sx * 0.35;
    const spar = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.05, 1.15, 5), armor);
    spar.rotation.z = sx * (Math.PI / 2 - 0.55);
    spar.position.set(sx * 0.48, 0.28, 0);
    wing.add(spar);
    // two ragged membrane triangles per wing (torn trailing edge)
    const paneA = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(sx * 1.0, 0.55, -0.06),
      new THREE.Vector3(sx * 0.62, -0.42, -0.1),
    ]).setIndex([0, 1, 2]);
    paneA.computeVertexNormals();
    wing.add(new THREE.Mesh(paneA, membrane));
    const paneB = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(sx * 0.62, -0.42, -0.1),
      new THREE.Vector3(sx * 1.0, 0.55, -0.06),
      new THREE.Vector3(sx * 1.15, -0.35, -0.14),
    ]).setIndex([0, 1, 2]);
    paneB.computeVertexNormals();
    wing.add(new THREE.Mesh(paneB, membrane));
    skel.torso.add(wing);
    wings.push(wing);
  }
  // pale gaunt head: swept-back hair, luminous red eyes
  const face = new THREE.Mesh(new THREE.SphereGeometry(0.24, 9, 7), pale);
  face.scale.set(0.86, 1.05, 0.92);
  skel.head.add(face);
  const hair = new THREE.Mesh(new THREE.SphereGeometry(0.25, 9, 7, 0, Math.PI * 2, 0, Math.PI / 2), armor);
  hair.position.set(0, 0.08, -0.06);
  hair.rotation.x = -0.35;
  skel.head.add(hair);
  const crownSpike = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.24, 4), crimson);
  crownSpike.position.set(0, 0.32, -0.1);
  skel.head.add(crownSpike);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.045, 6, 5), blood);
    eye.position.set(sx * 0.09, 0.03, 0.2);
    skel.head.add(eye);
  }
  // clawed hands; the blood-orb rides above the left palm
  addClaws(skel.fistL, pale, 0.34);
  addClaws(skel.fistR, pale, 0.44);
  const orb = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 8), blood);
  orb.position.set(0, -0.22, 0.12);
  skel.fistL.add(orb);
  const orbRing = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.02, 5, 16), crimson);
  orbRing.position.copy(orb.position);
  orbRing.rotation.x = Math.PI / 2 - 0.3;
  skel.fistL.add(orbRing);
  addBlobShadow(skel.group, 1.0);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, {
    strideAmp: 0.56,
    hipW: 0.32,
    dustColors: BONE_DUST,
    idleAccent: false,
    // wings breathe while idle and flare open at speed; the orb bobs over the palm
    idleMotion: (time, moveAmt) => {
      const flare = Math.sin(time * 1.55) * 0.07 * (1 - moveAmt) + moveAmt * 0.3;
      wings[0].rotation.z = 0.12 + flare;
      wings[1].rotation.z = -0.12 - flare;
      orb.position.y = -0.22 + Math.sin(time * 2.1) * 0.035;
      orbRing.rotation.z = time * 0.8;
    },
  });
  rig.stridePerMeter *= 1.2; // predatory quick steps
  rig.auraMats.push(blood);
  return rig;
}

/**
 * Fivefold Amalgam — Ashen apotheosis form B. A hulking siege horror stitched from five
 * bodies: one oversized fused arm, a slack-headed half-torso riding the left shoulder,
 * iron cage fragments in its back, hooks and chains, and five faint soul-glow orbs.
 */
function ahFivefoldAmalgam(owner: 0 | 1, uiScale: number): BossRig {
  const flesh = M(mix(0x7a5048, OWNER_COLORS[owner], 0.08), 0.95, 0.02);
  const fleshDark = M(0x543230, 0.98, 0);
  const fleshPale = M(0x9a7a70, 0.9, 0.02);
  const bone = M(0xcfc6b4, 0.8, 0.05);
  const iron = M(0x3a3438, 0.55, 0.5);
  const soul = glowM(0x8fe8a8, 1.35);
  const skel = buildBiped({
    thigh: 0.82, shin: 0.72, legR: 0.28, hipW: 0.6,
    upper: 1.12, fore: 1.02, armR: 0.23, fistR: 0.3,
    shoulderW: 1.08, shoulderY: 1.7, headY: 2.02, headZ: 0.3,
    legMat: fleshDark, armMat: flesh, fistMat: fleshDark,
  });
  // the right arm is two fused arms: scale the whole limb up and weld a second forearm on
  skel.shR.scale.setScalar(1.28);
  const fusedFore = new THREE.Mesh(taperCyl(0.16, 0.2, 0.95), fleshPale);
  fusedFore.position.set(0.2, -0.48, -0.05);
  fusedFore.rotation.z = -0.12;
  skel.elR.add(fusedFore);
  const fusedFist = new THREE.Mesh(new THREE.SphereGeometry(0.2, 7, 6), fleshDark);
  fusedFist.position.set(0.26, -0.98, -0.08);
  skel.elR.add(fusedFist);
  for (const cx of [-0.1, 0.02, 0.14]) {
    const knuckleSpike = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.26, 5), bone);
    knuckleSpike.position.set(cx, -0.2, 0.16);
    knuckleSpike.rotation.x = 0.6;
    skel.fistR.add(knuckleSpike);
  }
  // lumpy asymmetric torso
  const bulk = new THREE.Mesh(new THREE.CapsuleGeometry(0.72, 0.8, 4, 9), flesh);
  bulk.position.set(0, 1.25, 0);
  bulk.rotation.z = 0.06;
  skel.torso.add(bulk);
  const gutLump = new THREE.Mesh(new THREE.SphereGeometry(0.5, 8, 6), fleshPale);
  gutLump.position.set(0.28, 0.85, 0.18);
  skel.torso.add(gutLump);
  const backLump = new THREE.Mesh(new THREE.SphereGeometry(0.42, 8, 6), fleshDark);
  backLump.position.set(-0.3, 1.55, -0.3);
  skel.torso.add(backLump);
  // stitch seams where the five bodies meet
  for (let i = 0; i < 6; i++) {
    const stitch = new THREE.Mesh(new THREE.BoxGeometry(0.26, 0.04, 0.04), iron);
    stitch.position.set((i % 2 ? 1 : -1) * (0.18 + (i % 3) * 0.12), 0.85 + i * 0.22, 0.62 - (i % 3) * 0.06);
    stitch.rotation.z = (i % 2 ? 1 : -1) * 0.45;
    skel.torso.add(stitch);
  }
  // iron cage fragments embedded in the back
  for (const bx of [-0.34, -0.06, 0.24]) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.85, 0.07), iron);
    bar.position.set(bx, 1.7, -0.62);
    bar.rotation.x = 0.2;
    bar.rotation.z = bx * 0.5;
    skel.torso.add(bar);
  }
  const cageHoop = new THREE.Mesh(new THREE.TorusGeometry(0.4, 0.045, 5, 12, Math.PI * 1.2), iron);
  cageHoop.position.set(0, 1.85, -0.6);
  cageHoop.rotation.z = Math.PI * 0.9;
  skel.torso.add(cageHoop);
  // half-torso riding the left shoulder, its slack head lolling
  const rider = new THREE.Mesh(new THREE.CapsuleGeometry(0.24, 0.4, 3, 7), fleshPale);
  rider.position.set(-0.82, 2.08, -0.06);
  rider.rotation.z = 0.5;
  skel.torso.add(rider);
  const slackHead = new THREE.Group();
  slackHead.position.set(-1.05, 2.3, 0.02);
  skel.torso.add(slackHead);
  const slackSkull = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 6), fleshPale);
  slackSkull.scale.set(0.9, 1.02, 0.9);
  slackHead.add(slackSkull);
  for (const sx of [-1, 1]) {
    const shutEye = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.02, 0.02), fleshDark);
    shutEye.position.set(sx * 0.07, 0.02, 0.17);
    slackHead.add(shutEye);
  }
  const slackArm = new THREE.Mesh(taperCyl(0.07, 0.09, 0.6), fleshPale);
  slackArm.position.set(-1.02, 1.75, 0.12);
  slackArm.rotation.z = 0.35;
  skel.torso.add(slackArm);
  // hooks and chains: links down the oversized arm, hooks dangling
  for (let i = 0; i < 3; i++) {
    const link = new THREE.Mesh(new THREE.TorusGeometry(0.11, 0.028, 5, 8), iron);
    link.position.set(0.05, -0.25 - i * 0.24, 0.2);
    link.rotation.y = (i % 2) * Math.PI / 2;
    skel.shR.add(link);
  }
  for (const [hx, hy, hparent] of [
    [-0.15, -0.4, skel.fistL],
    [0.18, -0.35, skel.fistR],
  ] as const) {
    const hook = new THREE.Mesh(new THREE.TorusGeometry(0.1, 0.03, 5, 10, Math.PI * 1.4), iron);
    hook.position.set(hx, hy, 0.05);
    hook.rotation.z = Math.PI * 0.8;
    hparent.add(hook);
  }
  // main head: crude skull-faced knot, dwarfed by the shoulders
  const skullFace = new THREE.Mesh(new THREE.BoxGeometry(0.42, 0.4, 0.42), fleshDark);
  skel.head.add(skullFace);
  const jawPlate = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.14, 0.3), bone);
  jawPlate.position.set(0, -0.24, 0.1);
  skel.head.add(jawPlate);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 5), soul);
    eye.position.set(sx * 0.11, 0.05, 0.23);
    skel.head.add(eye);
  }
  // five faint soul-glow orbs embedded across the amalgam — one per sacrificed worker
  const soulSpots: Array<[THREE.Object3D, number, number, number, number]> = [
    [skel.torso, 0, 1.45, 0.68, 0.11],
    [skel.torso, 0.42, 1.0, 0.5, 0.09],
    [skel.torso, -0.5, 1.75, -0.42, 0.09],
    [skel.elR, 0.08, -0.5, 0.2, 0.08],
    [skel.torso, -0.85, 2.06, 0.18, 0.07],
  ];
  for (const [parent, ox, oy, oz, r] of soulSpots) {
    const orb = new THREE.Mesh(new THREE.SphereGeometry(r, 7, 6), soul);
    orb.position.set(ox, oy, oz);
    parent.add(orb);
  }
  const victim = addVictimProp(skel.fistL, 0.34);
  addBlobShadow(skel.group, 1.6);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, {
    canCrush: true,
    victim,
    hunch: 0.42,
    hipW: 0.6,
    strideAmp: 0.42,
    dustColors: BONE_DUST,
    // dragging gait accent: the slack head lolls, the chains breathe
    idleMotion: (time, moveAmt) => {
      slackHead.rotation.z = 0.55 + Math.sin(time * 0.8) * 0.09;
      slackHead.rotation.x = Math.sin(time * 0.55) * 0.07 + moveAmt * 0.15;
    },
  });
  rig.stridePerMeter *= 0.9; // long slow heaves
  rig.auraMats.push(soul);
  return rig;
}

// ---------------------------------------------------------------- Verdant Wilds

function vwThornback(owner: 0 | 1, uiScale: number): BossRig {
  const hide = M(mix(0x6a5236, OWNER_COLORS[owner], 0.08), 0.9, 0.05);
  const bark = M(0x54402a, 0.95, 0);
  const thorn = M(0x8a9a5a, 0.8, 0.05);
  const skel = buildBiped({
    thigh: 0.72, shin: 0.62, legR: 0.2, hipW: 0.5,
    upper: 0.98, fore: 0.9, armR: 0.2, fistR: 0.3,
    shoulderW: 0.95, shoulderY: 1.5, headY: 1.65, headZ: 0.42,
    legMat: hide, armMat: hide, fistMat: bark,
  });
  // hunched bulky torso
  const bulk = new THREE.Mesh(new THREE.SphereGeometry(0.85, 10, 8), hide);
  bulk.scale.set(1.0, 0.95, 1.15);
  bulk.position.set(0, 1.05, -0.1);
  skel.torso.add(bulk);
  // thorn ridge down the back
  for (let i = 0; i < 4; i++) {
    const spike = new THREE.Mesh(new THREE.ConeGeometry(0.12 + (3 - i) * 0.03, 0.55 + (3 - i) * 0.12, 5), thorn);
    spike.position.set(0, 1.75 - i * 0.32, -0.5 - i * 0.16);
    spike.rotation.x = -0.5 - i * 0.12;
    skel.torso.add(spike);
  }
  // boar-like head: snout + tusks
  const skull = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.42, 0.6), hide);
  skel.head.add(skull);
  const snout = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.26, 0.3), bark);
  snout.position.set(0, -0.08, 0.4);
  skel.head.add(snout);
  for (const sx of [-1, 1]) {
    const tusk = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.4, 5), M(0xe8dcc0, 0.6, 0.1));
    tusk.position.set(sx * 0.2, -0.1, 0.5);
    tusk.rotation.x = 0.9;
    skel.head.add(tusk);
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.045, 6, 5), glowM(0xffb03f, 1.2));
    eye.position.set(sx * 0.16, 0.1, 0.32);
    skel.head.add(eye);
  }
  // a Verdant warrior rides the razorback (owner request: mounts should read as ridden)
  const rmat = M(mix(0x4f6f34, OWNER_COLORS[owner], 0.22), 0.85, 0.05);
  const rlimb = M(mix(0x35521f, OWNER_COLORS[owner], 0.15), 0.85);
  const rider = new THREE.Group();
  rider.name = 'rider';
  const saddle = new THREE.Mesh(new THREE.BoxGeometry(0.95, 0.14, 0.8), M(OWNER_COLORS[owner], 0.75));
  saddle.position.set(0, -0.12, 0.02);
  rider.add(saddle);
  const rtorso = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.3, 0.68, 8), rmat);
  rtorso.position.y = 0.34;
  rtorso.rotation.x = 0.24;
  rider.add(rtorso);
  const rhead = new THREE.Mesh(new THREE.SphereGeometry(0.21, 10, 8), M(0xc99b72, 0.8));
  rhead.position.set(0, 0.82, 0.12);
  rider.add(rhead);
  const hood = new THREE.Mesh(new THREE.ConeGeometry(0.26, 0.5, 7), rmat);
  hood.position.set(0, 0.9, 0.06);
  rider.add(hood);
  for (const sx of [-1, 1]) {
    const th = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.09, 0.62, 6), rlimb);
    th.position.set(sx * 0.3, 0.05, 0.08);
    th.rotation.z = sx * 0.62;
    th.rotation.x = 0.5;
    rider.add(th);
  }
  // couched thorn-spear along the charge line
  const spear = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.06, 2.4, 6), M(0x6a5236, 0.85));
  spear.name = 'riderSpear';
  spear.rotation.x = Math.PI / 2;
  spear.position.set(0.3, 0.42, 1.05);
  rider.add(spear);
  const spearTip = new THREE.Mesh(new THREE.ConeGeometry(0.1, 0.42, 6), glowM(0x9aca5a, 0.5));
  spearTip.rotation.x = Math.PI / 2;
  spearTip.position.set(0.3, 0.42, 2.3);
  rider.add(spearTip);
  const rarm = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.07, 0.55, 6), rlimb);
  rarm.name = 'riderArm';
  rarm.position.set(0.28, 0.5, 0.35);
  rarm.rotation.x = 1.2;
  rider.add(rarm);
  rider.position.set(0, 1.5, -0.15);
  skel.torso.add(rider);

  addBlobShadow(skel.group, 1.4);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, {
    hunch: 0.5,
    hipW: 0.5,
    strideAmp: 0.55,
    dustColors: LEAF_DUST,
    idleAccent: false,
    // Keep the mount itself unchanged; only let the rider settle in the saddle and make tiny
    // corrections with the couched spear while idle.
    idleMotion: (time, moveAmt) => {
      const idle = 1 - moveAmt;
      const settle = Math.sin(time * 1.25) * idle;
      rider.position.y = 1.5 + settle * 0.025;
      rider.rotation.z = Math.sin(time * 0.62) * 0.018 * idle;
      spear.rotation.x = Math.PI / 2 + settle * 0.035;
      spearTip.rotation.x = Math.PI / 2 + settle * 0.035;
      rarm.rotation.z = Math.sin(time * 0.8 + 0.7) * 0.025 * idle;
    },
  });
  rig.stridePerMeter *= 1.15;
  return rig;
}

function vwAncientGroveback(owner: 0 | 1, uiScale: number): BossRig {
  const bark = M(mix(0x6a5236, OWNER_COLORS[owner], 0.06), 0.95, 0);
  const barkDark = M(0x4a3822, 0.95, 0);
  const leaf = new THREE.MeshStandardMaterial({ color: 0x5f8a3c, roughness: 1, flatShading: true });
  const amber = glowM(0xffc45e, 1.2);
  const skel = buildBiped({
    // The rear legs come from the biped scaffold; the lowered arms become the front pair.
    // Together they give the boss a broad, four-footed turtle gait instead of a humanoid tree.
    thigh: 0.62, shin: 0.56, legR: 0.28, hipW: 0.82,
    upper: 0.62, fore: 0.56, armR: 0.25, fistR: 0.32,
    shoulderW: 0.9, shoulderY: 0.58, headY: 1.86, headZ: 0.78,
    legMat: bark, armMat: bark, fistMat: barkDark,
  });
  skel.shL.position.z = 0.58;
  skel.shR.position.z = 0.58;
  const shell = new THREE.Mesh(new THREE.SphereGeometry(1.34, 12, 8), barkDark);
  shell.name = 'grovebackShell';
  shell.position.set(0, 0.78, -0.08);
  shell.scale.set(1.08, 0.62, 1.28);
  skel.torso.add(shell);
  const shellMoss = new THREE.Mesh(new THREE.SphereGeometry(1.13, 12, 7), leaf);
  shellMoss.position.set(0, 1.02, -0.13);
  shellMoss.scale.set(1.02, 0.18, 1.1);
  skel.torso.add(shellMoss);
  const shellRim = new THREE.Mesh(new THREE.TorusGeometry(1.22, 0.07, 6, 18), bark);
  shellRim.position.set(0, 0.8, -0.08);
  shellRim.scale.set(1.06, 1, 1.2);
  skel.torso.add(shellRim);
  const tail = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.72, 6), barkDark);
  tail.position.set(0, 0.45, -1.18);
  tail.rotation.x = -Math.PI / 2;
  skel.torso.add(tail);
  const trunk = new THREE.Mesh(taperCyl(0.62, 0.82, 1.9), bark);
  trunk.position.set(0, 1.45, -0.04);
  skel.torso.add(trunk);
  // moss shelf + canopy clumps riding the tree's neck
  addFoliage(skel.torso, leaf, -0.7, 2.25, 0.45, 0.5);
  addFoliage(skel.torso, leaf, 0.7, 2.3, 0.55, 0.45);
  addFoliage(skel.torso, leaf, 0, 2.55, 0.25, 0.58);
  // bark face with amber eyes
  const face = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.5, 0.4), barkDark);
  skel.head.add(face);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.055, 6, 5), amber);
    eye.position.set(sx * 0.12, 0.06, 0.22);
    skel.head.add(eye);
  }
  const beardMoss = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.4, 6), leaf);
  beardMoss.position.set(0, -0.35, 0.12);
  beardMoss.rotation.x = Math.PI;
  skel.head.add(beardMoss);
  const victim = addVictimProp(skel.fistL, 0.3);
  addBlobShadow(skel.group, 1.4);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, { canCrush: true, victim, hipW: 0.55, strideAmp: 0.46, dustColors: LEAF_DUST, idleAccent: false });
  rig.auraMats.push(amber);
  return rig;
}

function vwWorldrootTitan(owner: 0 | 1, uiScale: number): BossRig {
  const bark = M(mix(0x5a462e, OWNER_COLORS[owner], 0.06), 0.95, 0);
  const barkDark = M(0x3e3220, 0.95, 0);
  const leaf = new THREE.MeshStandardMaterial({ color: 0x4f7a3a, roughness: 1, flatShading: true });
  const heart = glowM(0x8fe05a, 1.6);
  const skel = buildBiped({
    thigh: 1.08, shin: 0.98, legR: 0.28, hipW: 0.52,
    upper: 1.0, fore: 0.92, armR: 0.22, fistR: 0.3,
    shoulderW: 1.0, shoulderY: 2.0, headY: 2.45,
    legMat: bark, armMat: bark, fistMat: barkDark,
  });
  const trunk = new THREE.Mesh(taperCyl(0.62, 0.85, 2.0), bark);
  trunk.position.y = 1.2;
  skel.torso.add(trunk);
  // glowing heartwood seams
  for (const [sy, sh] of [[1.5, 0.7], [0.85, 0.5]] as [number, number][]) {
    const seam = new THREE.Mesh(new THREE.BoxGeometry(0.1, sh, 0.06), heart);
    seam.position.set(0, sy, 0.62);
    skel.torso.add(seam);
  }
  const heartCore = new THREE.Mesh(new THREE.SphereGeometry(0.2, 8, 6), heart);
  heartCore.position.set(0, 1.7, 0.55);
  skel.torso.add(heartCore);
  // root flares at the ankles
  for (const knee of [skel.kneeL, skel.kneeR]) {
    const flare = new THREE.Mesh(new THREE.ConeGeometry(0.42, 0.6, 6), barkDark);
    flare.position.y = -0.75;
    knee.add(flare);
  }
  // antler branches + crown foliage
  const face = new THREE.Mesh(new THREE.BoxGeometry(0.44, 0.56, 0.44), barkDark);
  skel.head.add(face);
  for (const sx of [-1, 1]) {
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.05, 6, 5), heart);
    eye.position.set(sx * 0.12, 0.1, 0.24);
    skel.head.add(eye);
    const branchLow = new THREE.Mesh(new THREE.ConeGeometry(0.06, 0.8, 5), bark);
    branchLow.position.set(sx * 0.34, 0.55, -0.05);
    branchLow.rotation.z = sx * -0.6;
    skel.head.add(branchLow);
    const branchHigh = new THREE.Mesh(new THREE.ConeGeometry(0.045, 0.55, 5), bark);
    branchHigh.position.set(sx * 0.58, 0.85, -0.05);
    branchHigh.rotation.z = sx * -0.95;
    skel.head.add(branchHigh);
  }
  addFoliage(skel.head, leaf, 0, 0.75, -0.1, 0.4);
  // root-club fists
  for (const fist of [skel.fistL, skel.fistR]) {
    const club = new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.7, 6), barkDark);
    club.position.y = -0.3;
    fist.add(club);
  }
  const victim = addVictimProp(skel.fistL, 0.36);
  addBlobShadow(skel.group, 1.55);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, { canCrush: true, victim, strideAmp: 0.44, dustColors: LEAF_DUST });
  rig.auraMats.push(heart);
  return rig;
}

// ---------------------------------------------------------------- Astral Dominion

function adXyra(owner: 0 | 1, uiScale: number): BossRig {
  const white = M(mix(0xe0e6f4, OWNER_COLORS[owner], 0.08), 0.35, 0.3);
  const frame = M(0x8a9ac0, 0.4, 0.6);
  const blade = glowM(0x6fd8e8, 1.8);
  const skel = buildBiped({
    thigh: 0.82, shin: 0.78, legR: 0.11, hipW: 0.32,
    upper: 0.7, fore: 0.66, armR: 0.09, fistR: 0.13,
    shoulderW: 0.56, shoulderY: 1.6, headY: 1.92,
    legMat: frame, armMat: white, fistMat: frame,
  });
  const chest = new THREE.Mesh(new THREE.CapsuleGeometry(0.36, 0.65, 3, 8), white);
  chest.position.y = 1.2;
  skel.torso.add(chest);
  const sash = new THREE.Mesh(new THREE.BoxGeometry(0.14, 1.0, 0.5), blade);
  sash.position.set(0, 1.15, 0);
  sash.rotation.z = 0.5;
  skel.torso.add(sash);
  addCape(skel.torso, M(0x9fb8e8, 0.9, 0.05), 0.8, 1.3, 1.6);
  // crested helm
  const helm = new THREE.Mesh(new THREE.SphereGeometry(0.26, 9, 7), white);
  skel.head.add(helm);
  const crest = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.6, 5), blade);
  crest.position.y = 0.4;
  skel.head.add(crest);
  const visor = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.06, 0.1), blade);
  visor.position.set(0, 0.02, 0.24);
  skel.head.add(visor);
  // twin veil blades
  for (const fist of [skel.fistL, skel.fistR]) {
    const edge = new THREE.Mesh(new THREE.ConeGeometry(0.055, 1.35, 4), blade);
    edge.position.y = -0.75;
    edge.rotation.x = Math.PI;
    edge.rotation.y = Math.PI / 4;
    fist.add(edge);
  }
  addBlobShadow(skel.group, 0.95);
  enableShadows(skel.group);
  const rig = baseRig(skel, uiScale, { dualWield: true, strideAmp: 0.58, dustColors: LIGHT_DUST });
  rig.stridePerMeter *= 1.3;
  rig.auraMats.push(blade);
  return rig;
}

function adArbiter(owner: 0 | 1, uiScale: number): BossRig {
  const robe = M(mix(0xd0d8f0, OWNER_COLORS[owner], 0.1), 0.7, 0.1);
  const frame = M(0x8a9ac0, 0.4, 0.6);
  const crystal = glowM(0x6fd8e8, 1.6);

  const group = new THREE.Group();
  const root = new THREE.Group();
  const rootY = 1.0;
  root.position.y = rootY;
  group.add(root);
  const torso = new THREE.Group();
  root.add(torso);

  // flowing robe cone (no legs — the Arbiter floats)
  const skirt = new THREE.Mesh(taperCyl(0.42, 0.95, 1.7), robe);
  skirt.position.y = -0.35;
  torso.add(skirt);
  const hem = new THREE.Mesh(new THREE.TorusGeometry(0.92, 0.06, 6, 20), frame);
  hem.rotation.x = Math.PI / 2;
  hem.position.y = -1.18;
  torso.add(hem);
  const chest = new THREE.Mesh(new THREE.CapsuleGeometry(0.42, 0.55, 3, 8), robe);
  chest.position.y = 0.85;
  torso.add(chest);
  for (const sx of [-1, 1]) {
    const pauldron = new THREE.Mesh(new THREE.SphereGeometry(0.26, 8, 6), frame);
    pauldron.position.set(sx * 0.58, 1.15, 0);
    torso.add(pauldron);
  }
  const head = new THREE.Group();
  head.position.y = 1.62;
  torso.add(head);
  const hood = new THREE.Mesh(new THREE.SphereGeometry(0.27, 9, 7), robe);
  head.add(hood);
  const face = new THREE.Mesh(new THREE.SphereGeometry(0.16, 8, 6), crystal);
  face.position.set(0, -0.02, 0.14);
  head.add(face);
  const haloRing = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.035, 6, 22), crystal);
  haloRing.position.set(0, 0.22, -0.18);
  head.add(haloRing);

  // left arm folded (simple sleeve), right arm carries the judgment staff
  const sleeveL = new THREE.Mesh(taperCyl(0.13, 0.16, 0.85), robe);
  sleeveL.position.set(-0.55, 0.72, 0.15);
  sleeveL.rotation.z = 0.5;
  torso.add(sleeveL);
  const staffArm = new THREE.Group();
  staffArm.position.set(0.58, 1.1, 0);
  staffArm.rotation.x = -0.25;
  const sleeveR = new THREE.Mesh(taperCyl(0.14, 0.17, 0.9), robe);
  sleeveR.position.y = -0.45;
  staffArm.add(sleeveR);
  const staff = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 2.3, 6), frame);
  staff.position.set(0, -0.9, 0.35);
  staff.rotation.x = 0.35;
  staffArm.add(staff);
  const orb = new THREE.Mesh(new THREE.SphereGeometry(0.18, 9, 7), crystal);
  orb.position.set(0, 0.18, -0.05);
  staffArm.add(orb);
  const muzzleTip = new THREE.Object3D();
  muzzleTip.position.copy(orb.position);
  staffArm.add(muzzleTip);
  torso.add(staffArm);

  // slow judgment rings orbiting the robe
  const ringA = new THREE.Mesh(new THREE.TorusGeometry(1.15, 0.03, 5, 26), crystal);
  ringA.rotation.x = Math.PI / 2;
  ringA.position.y = 0.1;
  ringA.userData.spinAxis = 'z';
  ringA.userData.spinSpeed = 0.6;
  root.add(ringA);
  const ringB = new THREE.Mesh(new THREE.TorusGeometry(0.85, 0.025, 5, 22), frame);
  ringB.rotation.x = Math.PI / 2 + 0.35;
  ringB.position.y = 0.55;
  ringB.userData.spinAxis = 'z';
  ringB.userData.spinSpeed = -1.1;
  root.add(ringB);

  addBlobShadow(group, 1.15);
  enableShadows(group);
  const scale = uiScale / 1.7;
  group.scale.setScalar(scale);
  return {
    group, kind: 'hover', scale, big: uiScale >= 1.9, isMelee: false, canCrush: false, dualWield: false,
    dustColors: LIGHT_DUST, stridePerMeter: 0, strideAmp: 0, hipW: 0, legLen: 0, hunch: 0, rootY,
    root, torso, head, spinners: [ringA, ringB], cannonRestZ: 0, staffArm, muzzleTip,
    auraMats: [crystal], idleAccent: true,
  };
}

function adWarEngine(owner: 0 | 1, uiScale: number): BossRig {
  const hullMat = M(mix(0xdde4f4, OWNER_COLORS[owner], 0.12), 0.35, 0.65);
  const frame = M(0x7a8ab0, 0.4, 0.7);
  const crystal = glowM(0x6fd8e8, 1.8);

  const group = new THREE.Group();
  const root = new THREE.Group();
  const rootY = 1.35;
  root.position.y = rootY;
  group.add(root);
  const torso = new THREE.Group();
  root.add(torso);

  // hull: inverted keel + core sphere caged in gyro rings
  const keel = new THREE.Mesh(taperCyl(1.0, 0.35, 1.1), hullMat);
  keel.position.y = -0.5;
  torso.add(keel);
  const deck = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.0, 0.35, 10), frame);
  deck.position.y = 0.1;
  torso.add(deck);
  const core = new THREE.Mesh(new THREE.SphereGeometry(0.52, 12, 10), crystal);
  core.position.y = 0.75;
  torso.add(core);
  const gyroA = new THREE.Mesh(new THREE.TorusGeometry(0.78, 0.05, 6, 24), frame);
  gyroA.position.y = 0.75;
  gyroA.userData.spinAxis = 'x';
  gyroA.userData.spinSpeed = 1.4;
  torso.add(gyroA);
  const gyroB = new THREE.Mesh(new THREE.TorusGeometry(0.92, 0.04, 6, 24), hullMat);
  gyroB.position.y = 0.75;
  gyroB.rotation.y = Math.PI / 2;
  gyroB.userData.spinAxis = 'y';
  gyroB.userData.spinSpeed = -0.9;
  torso.add(gyroB);
  const head = new THREE.Group();
  head.position.y = 1.3;
  torso.add(head);

  // orbiting targeting pylons
  const carousel = new THREE.Group();
  carousel.position.y = 0.4;
  carousel.userData.spinAxis = 'y';
  carousel.userData.spinSpeed = 0.8;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const pylon = new THREE.Mesh(new THREE.OctahedronGeometry(0.22, 0), crystal);
    pylon.position.set(Math.cos(a) * 1.55, 0, Math.sin(a) * 1.55);
    carousel.add(pylon);
    const strut = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.03, 1.1, 5), frame);
    strut.position.set(Math.cos(a) * 0.85, 0, Math.sin(a) * 0.85);
    strut.rotation.z = Math.PI / 2;
    strut.rotation.y = -a;
    carousel.add(strut);
  }
  root.add(carousel);

  // gimballed solar cannon (yaws to target; inner barrel recoils)
  const cannon = new THREE.Group();
  cannon.position.y = 1.35;
  const mount = new THREE.Mesh(new THREE.SphereGeometry(0.34, 9, 7), frame);
  cannon.add(mount);
  const cannonInner = new THREE.Group();
  const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.13, 0.17, 2.4, 8), hullMat);
  barrel.rotation.x = Math.PI / 2;
  barrel.position.z = 1.2;
  cannonInner.add(barrel);
  const focusRing = new THREE.Mesh(new THREE.TorusGeometry(0.24, 0.05, 6, 12), crystal);
  focusRing.position.z = 2.3;
  cannonInner.add(focusRing);
  const muzzleTip = new THREE.Object3D();
  muzzleTip.position.z = 2.45;
  cannonInner.add(muzzleTip);
  cannon.add(cannonInner);
  root.add(cannon);

  addBlobShadow(group, 1.5);
  enableShadows(group);
  const scale = uiScale / 1.7;
  group.scale.setScalar(scale);
  return {
    group, kind: 'hover', scale, big: true, isMelee: false, canCrush: false, dualWield: false,
    dustColors: LIGHT_DUST, stridePerMeter: 0, strideAmp: 0, hipW: 0, legLen: 0, hunch: 0, rootY,
    root, torso, head, spinners: [gyroA, gyroB, carousel], cannon, cannonInner, cannonRestZ: 0, muzzleTip,
    auraMats: [crystal], idleAccent: true,
  };
}

// ---------------------------------------------------------------- factory

export function makeBossRig(defId: string, owner: PlayerId, uiScale: number): BossRig {
  const slot = owner as 0 | 1;
  switch (defId) {
    case 'boss-ic-arcturus': return icArcturus(slot, uiScale);
    case 'boss-ic-iron-saint': return icIronSaint(slot, uiScale);
    case 'boss-ic-crown-colossus': return icCrownColossus(slot, uiScale);
    case 'boss-ah-veyr': return ahVeyr(slot, uiScale);
    case 'boss-ah-gravebound-colossus': return ahGraveboundColossus(slot, uiScale);
    case 'boss-ah-hollow-king': return ahHollowKing(slot, uiScale);
    // Ashen apotheosis forms are unit-id bosses (isBoss on the UnitDef), hence u- ids here
    case 'u-ah-crimson-sovereign': return ahCrimsonSovereign(slot, uiScale);
    case 'u-ah-fivefold-amalgam': return ahFivefoldAmalgam(slot, uiScale);
    case 'boss-vw-thornback': return vwThornback(slot, uiScale);
    case 'boss-vw-ancient-groveback': return vwAncientGroveback(slot, uiScale);
    case 'boss-vw-worldroot-titan': return vwWorldrootTitan(slot, uiScale);
    case 'boss-ad-xyra': return adXyra(slot, uiScale);
    case 'boss-ad-arbiter': return adArbiter(slot, uiScale);
    case 'boss-ad-war-engine': return adWarEngine(slot, uiScale);
    default: return icArcturus(slot, uiScale); // unknown boss: armored-commander fallback
  }
}

// ---------------------------------------------------------------- animation

const tmpV = new THREE.Vector3();

function easeOut(k: number): number {
  return 1 - (1 - k) * (1 - k);
}

/**
 * Drives one boss rig for a frame. The caller has already set group position/rotation;
 * this poses the joints and returns world-space events (footfalls, slam impacts, crush
 * bursts, muzzle flashes) for the renderer to turn into particles/rings/camera rumble.
 */
export function animateBoss(rig: BossRig, a: BossAnimState, inp: BossFrameInput): BossFrameEvents {
  const ev: BossFrameEvents = {};
  const { t, dt } = inp;

  a.attackT += dt;
  if (inp.fired) {
    a.attackT = 0;
    a.impactDone = false;
    a.parity ^= 1;
    a.flash = 1;
  }
  a.flash *= Math.exp(-dt * 6);

  // summon entrance: giants grow into place
  a.spawnT = Math.min(1, a.spawnT + dt / 0.7);
  rig.group.scale.setScalar(rig.scale * (0.7 + 0.3 * easeOut(a.spawnT)));

  // crush flourish scheduling
  if (inp.startCrush && a.crushT >= CRUSH_DUR) {
    a.crushT = 0;
    a.crushBurstDone = false;
  } else if (a.crushT < CRUSH_DUR) {
    a.crushT += dt;
  }

  // aura pulse: idle shimmer + windup surge + fire flash
  for (const m of rig.auraMats) {
    const base = (m.userData.baseEI as number) ?? 1;
    m.emissiveIntensity = base * (1 + 0.25 * Math.sin(t * 2.6 + a.seed)) * (1 + 1.1 * inp.winding) + a.flash * 2.2;
  }

  const breathe = Math.sin(t * 1.4 + a.seed);

  if (rig.kind === 'hover') {
    animateHover(rig, a, inp, ev, breathe);
    return ev;
  }

  // ---- gait
  const moveK = inp.moveAmt;
  const prevCos = Math.cos(a.walkPhase);
  a.walkPhase += inp.speed * dt * rig.stridePerMeter;
  const s1 = Math.sin(a.walkPhase);
  const c1 = Math.cos(a.walkPhase);
  // a foot plants where |sin| peaks — that's where cos crosses zero
  if (moveK > 0.15 && prevCos * c1 <= 0 && prevCos !== c1) {
    const side = s1 > 0 ? -1 : 1; // the leg that just landed
    const perp = inp.facing + Math.PI / 2;
    ev.footfall = {
      x: inp.x + Math.cos(perp) * side * rig.hipW * rig.scale,
      z: inp.z + Math.sin(perp) * side * rig.hipW * rig.scale,
    };
  }

  const stride = rig.strideAmp * moveK;
  const bob = -Math.abs(s1) * 0.05 * rig.legLen * moveK + breathe * 0.02;
  rig.root.position.y = rig.rootY + bob;
  rig.hipL!.rotation.x = -s1 * stride;
  rig.hipR!.rotation.x = s1 * stride;
  // knee lifts mid-swing (left leg swings forward while cos > 0)
  rig.kneeL!.rotation.x = Math.max(0, c1) * stride * 1.3;
  rig.kneeR!.rotation.x = Math.max(0, -c1) * stride * 1.3;

  // ---- attack overlay (weapon arm timeline)
  let combatArm: number | null = null;
  let combatElbow = -0.55;
  let twist = 0;
  let lean = 0;
  if (a.attackT < SLAM_T) {
    const k = a.attackT / SLAM_T;
    const e = k * k;
    combatArm = ARM_RAISED + (ARM_SLAM_END - ARM_RAISED) * e; // over the top of the arc
    combatElbow = -1.15 + 1.0 * e;
    twist = 0.45 - 0.95 * e;
    lean = -0.12 + 0.42 * e;
    if (!a.impactDone && a.attackT >= SLAM_T - 0.03) {
      a.impactDone = true;
      if (rig.isMelee) {
        ev.impact = {
          x: inp.x + Math.cos(inp.facing) * inp.reach,
          z: inp.z + Math.sin(inp.facing) * inp.reach,
        };
      }
    }
  } else if (a.attackT < RECOVER_T) {
    const k = (a.attackT - SLAM_T) / (RECOVER_T - SLAM_T);
    const e = k * (2 - k);
    combatArm = ARM_SLAM_REC + (ARM_REST - ARM_SLAM_REC) * e; // lower from forward-down back to rest
    combatElbow = -0.15 - 0.4 * e;
    twist = -0.5 * (1 - e);
    lean = 0.3 * (1 - e);
  }
  if (inp.winding > 0) {
    const w = inp.winding * inp.winding;
    const from = combatArm ?? ARM_REST;
    combatArm = from + (ARM_RAISED - from) * w;
    combatElbow = combatElbow + (-1.15 - combatElbow) * w;
    twist += 0.45 * w;
    lean += -0.12 * w;
  }

  // which arm swings the weapon this attack
  const weaponLeft = rig.dualWield && a.parity === 1;
  const twistSign = weaponLeft ? -1 : 1;

  // ---- torso / head
  rig.torso.rotation.x = rig.hunch + 0.12 * moveK + lean + breathe * 0.015;
  rig.torso.rotation.z = s1 * 0.05 * moveK;
  rig.torso.rotation.y = -s1 * 0.08 * moveK + twist * twistSign;
  if (inp.targetYaw !== null) {
    rig.head.rotation.y = lerpAngle(rig.head.rotation.y, Math.max(-0.8, Math.min(0.8, wrapPi(inp.facing - inp.targetYaw))), dt * 6);
  } else {
    rig.head.rotation.y = lerpAngle(rig.head.rotation.y, Math.sin(t * 0.5 + a.seed) * 0.3 * (1 - moveK), dt * 3);
  }
  rig.head.rotation.x = -rig.hunch * 0.55 - 0.05 * moveK;

  // ---- arms: walk counter-swing, overridden by combat/crush timelines
  const idleSwing = Math.sin(t * 1.4 + a.seed) * 0.05;
  const swingL = ARM_REST + s1 * stride * 0.7 + idleSwing;
  const swingR = ARM_REST - s1 * stride * 0.7 + idleSwing;

  const crushing = rig.canCrush && a.crushT < CRUSH_DUR;
  let armLX = swingL;
  let elLX = -0.5;
  let armRX = swingR;
  let elRX = -0.55;

  if (combatArm !== null || inp.winding > 0) {
    if (weaponLeft) {
      armLX = combatArm ?? swingL;
      elLX = combatElbow;
    } else {
      armRX = combatArm ?? swingR;
      elRX = combatElbow;
    }
  }

  // crush flourish: the off-hand reaches down, grabs, lifts, squeezes, discards
  if (crushing && rig.fistL && rig.victim) {
    const ct = a.crushT;
    if (ct < 0.35) {
      // reach forward-down toward the ground
      const k = easeOut(ct / 0.35);
      armLX = ARM_REST + (-1.1 - ARM_REST) * k;
      elLX = -0.5 + 0.35 * k;
      rig.torso.rotation.x += 0.3 * k;
    } else if (ct < 0.75) {
      // hoist the grabbed soldier up in front of the chest
      const k = easeOut((ct - 0.35) / 0.4);
      armLX = -1.1 + (-0.6 - -1.1) * k;
      elLX = -0.15 - 1.4 * k;
      rig.torso.rotation.x += 0.3 * (1 - k);
      rig.victim.visible = true;
    } else if (ct < 1.0) {
      const k = (ct - 0.75) / 0.25;
      armLX = -0.6;
      elLX = -1.55;
      const squeeze = 1 + 0.3 * Math.sin(k * Math.PI * 3);
      rig.fistL.scale.setScalar(squeeze);
      rig.victim.scale.set(1, Math.max(0.25, 1 - k * 0.9), 1);
      if (!a.crushBurstDone && k > 0.85) {
        a.crushBurstDone = true;
        rig.victim.visible = false;
        rig.fistL.getWorldPosition(tmpV);
        ev.crush = { x: tmpV.x, y: tmpV.y, z: tmpV.z };
      }
    } else {
      const k = easeOut((ct - 1.0) / 0.5);
      armLX = -0.6 + (swingL - -0.6) * k;
      elLX = -1.55 + (-0.5 - -1.55) * k;
      rig.fistL.scale.setScalar(1);
      rig.victim.scale.set(1, 1, 1);
      rig.victim.visible = false;
    }
  } else if (rig.victim) {
    rig.victim.visible = false;
    rig.fistL?.scale.setScalar(1);
  }

  rig.shL!.rotation.x = armLX;
  rig.elL!.rotation.x = elLX;
  rig.shR!.rotation.x = armRX;
  rig.elR!.rotation.x = elRX;
  // keep elbows tucked slightly outward for bulk
  rig.shL!.rotation.z = 0.12;
  rig.shR!.rotation.z = -0.12;

  if (rig.idleAccent && moveK < 0.12 && combatArm === null && !crushing) {
    const idle = 1 - moveK / 0.12;
    const shift = Math.sin(t * 0.62 + a.seed) * idle;
    rig.root.rotation.z = shift * 0.014;
    rig.head.rotation.z = -shift * 0.035;
    rig.shL!.rotation.z += shift * 0.025;
    rig.shR!.rotation.z += shift * 0.018;
    rig.elL!.rotation.z = Math.sin(t * 0.9 + a.seed) * 0.018 * idle;
    rig.elR!.rotation.z = -Math.sin(t * 0.82 + a.seed) * 0.018 * idle;
  } else {
    rig.root.rotation.z = 0;
    rig.head.rotation.z = 0;
    rig.elL!.rotation.z = 0;
    rig.elR!.rotation.z = 0;
  }

  rig.idleMotion?.(t, moveK);

  return ev;
}

function animateHover(rig: BossRig, a: BossAnimState, inp: BossFrameInput, ev: BossFrameEvents, breathe: number): void {
  const { t, dt } = inp;
  const idle = 1 - inp.moveAmt;
  rig.root.position.y = rig.rootY + Math.sin(t * 1.7 + a.seed) * 0.12 + inp.moveAmt * 0.1;
  rig.root.rotation.x = 0.12 * inp.moveAmt;
  rig.root.rotation.z = breathe * 0.02;
  rig.torso.rotation.y = Math.sin(t * 0.52 + a.seed) * 0.035 * idle;
  rig.head.rotation.y = Math.sin(t * 0.68 + a.seed * 0.7) * 0.16 * idle;
  rig.head.rotation.x = Math.sin(t * 1.1 + a.seed) * 0.025 * idle;

  for (const sp of rig.spinners) {
    const axis = (sp.userData.spinAxis as 'x' | 'y' | 'z') ?? 'y';
    const speed = (sp.userData.spinSpeed as number) ?? 1;
    sp.rotation[axis] += dt * speed * (1 + inp.winding * 1.5 + a.flash * 2);
  }

  // gimbal cannon tracks the target (same convention as the tank turret)
  if (rig.cannon && inp.targetYaw !== null) {
    rig.cannon.rotation.y = lerpAngle(rig.cannon.rotation.y, inp.facing - inp.targetYaw, dt * 6);
  }
  if (rig.cannonInner) {
    const recoil = Math.max(0, 1 - a.attackT / 0.35);
    rig.cannonInner.position.z = rig.cannonRestZ - 0.5 * Math.sin(recoil * Math.PI);
  }
  if (rig.staffArm) {
    rig.staffArm.rotation.x = -0.25 - 1.35 * inp.winding - 0.5 * a.flash;
  }
  if (inp.fired && rig.muzzleTip) {
    rig.muzzleTip.getWorldPosition(tmpV);
    ev.muzzle = { x: tmpV.x, y: tmpV.y, z: tmpV.z };
  }
}
